// You'll need to get a free API key from https://openweathermap.org/api
// Add REACT_APP_OPENWEATHER_API_KEY to your .env file
const WEATHER_API_KEY = process.env.REACT_APP_OPENWEATHER_API_KEY;
const WEATHER_BASE_URL = 'https://api.openweathermap.org/data/2.5';

// Cache for weather data (coordinates -> weather data)
const weatherCache = new Map();
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes in milliseconds

// Track if weather has been loaded for each user session
const weatherLoadedForUser = new Map();

export const weatherService = {
  isWeatherConfigured() {
    return !!(WEATHER_API_KEY && WEATHER_API_KEY !== 'your_api_key_here');
  },

  // Check if weather has been loaded for this user session
  hasWeatherLoaded(userId) {
    return weatherLoadedForUser.get(userId) || false;
  },

  // Mark weather as loaded for this user session
  markWeatherLoaded(userId) {
    weatherLoadedForUser.set(userId, true);
  },

  // Reset weather loaded state (for manual refresh)
  resetWeatherLoaded(userId) {
    weatherLoadedForUser.set(userId, false);
  },

  async getCurrentWeather(lat, lng, userId = null) {
    if (!WEATHER_API_KEY) {
      throw new Error('Weather API key not configured. Please add REACT_APP_OPENWEATHER_API_KEY to your .env file.');
    }
    
    if (WEATHER_API_KEY === 'your_api_key_here') {
      throw new Error('Please replace "your_api_key_here" with your actual OpenWeatherMap API key in the .env file.');
    }

    // Round coordinates to reduce cache misses for nearby locations (~20km precision)
    const roundedLat = Math.round(lat * 10) / 10;
    const roundedLng = Math.round(lng * 10) / 10;
    const cacheKey = `${roundedLat},${roundedLng}`;

    // Check cache first
    const cached = weatherCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
      console.log('Using cached weather data for:', cacheKey);
      return cached.data;
    }

    // Direct fetch (no debounce) – caching prevents spam
    const url = `${WEATHER_BASE_URL}/weather?lat=${roundedLat}&lon=${roundedLng}&appid=${WEATHER_API_KEY}&units=imperial`;
    console.log('Fetching weather from:', url.replace(WEATHER_API_KEY, '***HIDDEN***'));

    const response = await fetch(url);
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Weather API response:', response.status, errorText);
      if (response.status === 401) {
        throw new Error('Invalid API key. Please check your OpenWeatherMap API key.');
      } else if (response.status === 429) {
        throw new Error('API rate limit exceeded. Please try again later.');
      } else {
        throw new Error(`Weather API error: ${response.status} - ${errorText}`);
      }
    }

    const data = await response.json();
    const weatherData = {
      temperature: Math.round(data.main.temp),
      feels_like: Math.round(data.main.feels_like),
      humidity: data.main.humidity,
      wind_speed: Math.round(data.wind.speed),
      wind_direction: data.wind.deg || 0,
      description: data.weather[0].description,
      icon: data.weather[0].icon,
      main: data.weather[0].main
    };

    // Cache the result
    weatherCache.set(cacheKey, {
      data: weatherData,
      timestamp: Date.now()
    });

    // Clean up old cache entries (older than 1 hour)
    const oneHourAgo = Date.now() - (60 * 60 * 1000);
    for (const [key, value] of weatherCache.entries()) {
      if (value.timestamp < oneHourAgo) {
        weatherCache.delete(key);
      }
    }

    return weatherData;
  },

  getWeatherIcon(iconCode) {
    // Map OpenWeatherMap icon codes to weather icons
    const iconMap = {
      '01d': '☀️', // clear sky day
      '01n': '🌙', // clear sky night
      '02d': '⛅', // few clouds day
      '02n': '☁️', // few clouds night
      '03d': '☁️', // scattered clouds
      '03n': '☁️', // scattered clouds
      '04d': '☁️', // broken clouds
      '04n': '☁️', // broken clouds
      '09d': '🌧️', // shower rain
      '09n': '🌧️', // shower rain
      '10d': '🌦️', // rain day
      '10n': '🌧️', // rain night
      '11d': '⛈️', // thunderstorm
      '11n': '⛈️', // thunderstorm
      '13d': '🌨️', // snow
      '13n': '🌨️', // snow
      '50d': '🌫️', // mist
      '50n': '🌫️', // mist
    };
    
    return iconMap[iconCode] || '🌤️';
  },

  getWeatherDescription(description) {
    // Capitalize first letter of each word
    return description.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  },

  getWindDirection(degrees) {
    // Convert degrees to cardinal direction
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    const index = Math.round(degrees / 22.5) % 16;
    return directions[index];
  },

  // Get weather data for hunt time periods
  async getHuntWeatherData(lat, lng, date, timePeriods) {
    if (!WEATHER_API_KEY) {
      throw new Error('Weather API key not configured');
    }

    const weatherData = {};
    
    // Map time periods to approximate times for weather data
    const timeMap = {
      'morning': '09:00',
      'midday': '12:00', 
      'afternoon': '16:00',
      'all_day': '12:00' // Use noon for all-day hunts
    };

    try {
      // For each time period, get weather data
      for (const period of timePeriods) {
        const time = timeMap[period];
        if (!time) continue;

        // Create timestamp for the specific date and time
        const timestamp = Math.floor(new Date(`${date}T${time}:00`).getTime() / 1000);
        
        // Use OpenWeatherMap historical data API (requires paid plan)
        // For now, we'll use current weather as a fallback
        const url = `${WEATHER_BASE_URL}/weather?lat=${lat}&lon=${lng}&appid=${WEATHER_API_KEY}&units=imperial`;
        
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Weather API error: ${response.status}`);
        }
        
        const data = await response.json();
        
        weatherData[period] = {
          temperature: Math.round(data.main.temp),
          feels_like: Math.round(data.main.feels_like),
          humidity: data.main.humidity,
          wind_speed: Math.round(data.wind.speed),
          wind_direction: data.wind.deg || 0,
          wind_direction_text: this.getWindDirection(data.wind.deg || 0),
          description: data.weather[0].description,
          icon: data.weather[0].icon,
          main: data.weather[0].main,
          timestamp: timestamp,
          time_period: period
        };
      }
      
      return weatherData;
    } catch (error) {
      console.error('Failed to fetch hunt weather data:', error);
      throw error;
    }
  }
};
